/**
 * MyCraftzIntroPage — /mycraftz/intro  (ICRAFTZ 2.0 — Page 4 of mockup)
 *
 * THE EMOTIONAL & PHILOSOPHICAL CENTER OF THE PLATFORM.
 *
 * Layout (parchment-primary, walnut accent panel for "HOW IT WORKS"):
 *  1. Top nav (BACK / MYCRAFTZ DASHBOARD)
 *  2. Hero — "MYCRAFTZ™ / From Reading to Identity." (Playfair, walnut)
 *  3. 5-stage chakra around a central brass tree emblem (Jigyasa → Siddhi)
 *  4. Walnut accent panel "HOW IT WORKS" — 6 bullets in 2 columns
 *  5. 5 vintage icon tiles (Curated Ideas / Actionable Plans / Daily Practice /
 *     Track Your Progress / Achieve Siddhi)
 *  6. Closing ribbon "ONE BOOK. ONE IDEA. ONE PRACTICE. ONE TRANSFORMATION."
 */
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "motion/react";
import {
  ArrowLeft, ArrowRight, BookOpen, Compass, CalendarCheck,
  TrendingUp, Crown, ScrollText, Sparkles, CheckCircle2, Eye, Target,
} from "lucide-react";
import { GlobalChromeRibbon } from "../components/GlobalChrome";
import "../styles/Parchment.css";
import "../styles/MyCraftzIntroPage.css";

const STAGES = [
  { key: "jigyasa",  devanagari: "जिज्ञासा", title: "JIGYASA",  sub: "Curiosity",   desc: "Be curious. Ask the right questions." },
  { key: "bodha",    devanagari: "बोध",       title: "BODHA",    sub: "Understanding", desc: "Understand timeless wisdom." },
  { key: "sankalpa", devanagari: "संकल्प",    title: "SANKALPA", sub: "Commitment",  desc: "Commit to one idea that matters most." },
  { key: "sadhana",  devanagari: "साधना",     title: "SADHANA",  sub: "Practice",    desc: "Practice it daily. Build the habit." },
  { key: "siddhi",   devanagari: "सिद्धि",    title: "SIDDHI",   sub: "Transformation", desc: "Transform your identity and your life." },
];

const HOW_IT_WORKS = [
  { icon: ScrollText,    text: "Discover a life-changing idea" },
  { icon: CalendarCheck, text: "Practice with a 30-day blueprint" },
  { icon: Eye,           text: "Understand it at a core level" },
  { icon: Sparkles,      text: "Transform your identity" },
  { icon: CheckCircle2,  text: "Commit to one idea" },
  { icon: Target,        text: "Track progress and achieve Siddhi" },
];

const VALUE_TILES = [
  { icon: BookOpen,    label: "CURATED IDEAS",      sub: "Hand-picked ideas from 49+ great books and curated reflections." },
  { icon: Compass,     label: "ACTIONABLE PLANS",   sub: "Get a personalised 30-day plan to turn ideas into daily action." },
  { icon: CalendarCheck, label: "DAILY PRACTICE",   sub: "Simple daily practices to build lasting habits effortlessly." },
  { icon: TrendingUp,  label: "TRACK YOUR PROGRESS", sub: "Visual dashboard to track your journey, streaks, consistency and transformation." },
  { icon: Crown,       label: "ACHIEVE SIDDHI",     sub: "Reach a new level of identity and experience lasting change." },
];

/* ──────────────────────────────────────────────────────────────
   Brass mandala — Jigyasa → Siddhi around a central tree emblem.
   ────────────────────────────────────────────────────────────── */
function ChakraMandala() {
  const size = 560;
  const cx = size / 2;
  const cy = size / 2;
  const R = 200;
  const angleFor = (i) => -90 + (i * 360) / STAGES.length;
  const polar = (ang, r = R) => {
    const a = (ang * Math.PI) / 180;
    return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
  };

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      className="mci-mandala"
      role="img"
      aria-label="MyCraftz five-stage transformation chakra"
      data-testid="mci-mandala"
      style={{ overflow: "visible" }}
    >
      <defs>
        <radialGradient id="mci-tree-bg" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#F3E5AB" />
          <stop offset="60%" stopColor="#D4AF37" />
          <stop offset="100%" stopColor="#8B6508" />
        </radialGradient>
        <radialGradient id="mci-node-brass" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#F3E5AB" />
          <stop offset="55%" stopColor="#D4AF37" />
          <stop offset="100%" stopColor="#8B6508" />
        </radialGradient>
      </defs>

      {/* Faint outer ring */}
      <circle cx={cx} cy={cy} r={R + 30} fill="none" stroke="rgba(139, 101, 8, 0.18)" strokeWidth="1" />
      {/* Dashed orbit */}
      <circle cx={cx} cy={cy} r={R} fill="none" stroke="rgba(139, 101, 8, 0.55)" strokeWidth="1.5" strokeDasharray="2 7" />

      {/* Curved arrow connectors between consecutive stages */}
      {STAGES.map((_, i) => {
        const a1 = angleFor(i);
        const a2 = angleFor((i + 1) % STAGES.length);
        const p1 = polar(a1, R - 6);
        const p2 = polar(a2, R - 6);
        // Slight outward bow for an "orbit" feel
        const midAng = (a1 + a2) / 2;
        const mp = polar(midAng, R + 8);
        return (
          <path
            key={`arc-${i}`}
            d={`M ${p1.x} ${p1.y} Q ${mp.x} ${mp.y} ${p2.x} ${p2.y}`}
            stroke="#8B6508"
            strokeWidth="1.2"
            fill="none"
            strokeLinecap="round"
            markerEnd="url(#mci-arrow)"
            opacity="0.7"
          />
        );
      })}
      <defs>
        <marker id="mci-arrow" viewBox="0 0 10 10" refX="8" refY="5"
                markerWidth="5" markerHeight="5" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#8B6508" />
        </marker>
      </defs>

      {/* Central tree emblem */}
      <g transform={`translate(${cx} ${cy})`}>
        <circle r="78" fill="url(#mci-tree-bg)" stroke="#8B6508" strokeWidth="1.5" />
        <circle r="66" fill="#2C1A12" stroke="#D4AF37" strokeWidth="1" />
        <g stroke="#D4AF37" strokeWidth="1.6" fill="none" strokeLinecap="round">
          <line x1="0" y1="36" x2="0" y2="-8" />
          <path d="M -28 -10 Q 0 -54 28 -10" />
          <path d="M -40 0 Q 0 -46 40 0" />
          <path d="M -22 -20 Q 0 -42 22 -20" />
          <line x1="0" y1="36" x2="-14" y2="48" />
          <line x1="0" y1="36" x2="14" y2="48" />
          <line x1="0" y1="36" x2="-24" y2="44" />
          <line x1="0" y1="36" x2="24" y2="44" />
        </g>
        <circle r="2.5" cx="-10" cy="-32" fill="#F3E5AB" />
        <circle r="2.5" cx="10" cy="-32" fill="#F3E5AB" />
        <circle r="2.5" cx="0" cy="-46" fill="#F3E5AB" />
      </g>

      {/* 5 stage nodes */}
      {STAGES.map((s, i) => {
        const a = angleFor(i);
        const { x, y } = polar(a);
        const labelR = R + 64;
        const lp = polar(a, labelR);
        let anchor = "middle";
        if (lp.x > cx + 24) anchor = "start";
        else if (lp.x < cx - 24) anchor = "end";
        return (
          <g key={s.key} data-testid={`mci-node-${s.key}`}>
            <circle cx={x} cy={y} r="24" fill="url(#mci-node-brass)" stroke="#8B6508" strokeWidth="1.4" />
            <circle cx={x} cy={y} r="16" fill="#2C1A12" stroke="#D4AF37" strokeWidth="1" />
            <text x={x} y={y + 5} textAnchor="middle"
                  fontFamily="'Playfair Display', Georgia, serif"
                  fontWeight="800" fontSize="14" fill="#D4AF37">
              {i + 1}
            </text>
            <text x={lp.x} y={lp.y - 14} textAnchor={anchor}
                  fontFamily="'Playfair Display', Georgia, serif"
                  fontWeight="800" fontSize="15" letterSpacing="0.18em" fill="#2C1A12">
              {s.title}
            </text>
            <text x={lp.x} y={lp.y + 2} textAnchor={anchor}
                  fontFamily="'Noto Serif Devanagari', serif"
                  fontSize="14" fill="#5C4033">
              {s.devanagari}
            </text>
            <text x={lp.x} y={lp.y + 20} textAnchor={anchor}
                  fontFamily="'Playfair Display', Georgia, serif"
                  fontStyle="italic" fontSize="12" fill="#5C4033">
              {s.sub}
            </text>
            <text x={lp.x} y={lp.y + 38} textAnchor={anchor}
                  fontFamily="'Lora', Georgia, serif" fontSize="11" fill="#8B7355">
              <tspan>{s.desc.split('. ')[0]}.</tspan>
            </text>
            {s.desc.split('. ').length > 1 && (
              <text x={lp.x} y={lp.y + 52} textAnchor={anchor}
                    fontFamily="'Lora', Georgia, serif" fontSize="11" fill="#8B7355">
                <tspan>{s.desc.split('. ').slice(1).join('. ')}</tspan>
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

export default function MyCraftzIntroPage() {
  const navigate = useNavigate();

  return (
    <main className="parchment-theme mci-page" data-testid="mycraftz-intro-page">
      {/* Top nav */}
      <div className="mci-nav">
        <button type="button" className="pt-btn-walnut" onClick={() => navigate("/")} data-testid="mci-back-home">
          <ArrowLeft size={14} /> Back to iCraftz
        </button>
        <Link to="/mycraftz" className="pt-link" data-testid="mci-go-mycraftz">
          MyCraftz Dashboard <ArrowRight size={12} style={{ verticalAlign: "middle" }} />
        </Link>
      </div>

      {/* Hero */}
      <header className="mci-hero">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mci-hero-inner"
        >
          <span className="pt-eyebrow">THE TRANSFORMATION ENGINE</span>
          <h1 className="mci-title">MYCRAFTZ<sup className="mci-tm">™</sup></h1>
          <p className="pt-tagline">From Reading to Identity.</p>
          <p className="pt-body mci-lede">
            MyCraftz<sup>™</sup> is your personal transformation engine. It converts powerful ideas
            from great books into practical habits and a new identity.
          </p>
          <div className="pt-divider"><span /><em>❦</em><span /></div>
        </motion.div>
      </header>

      {/* Mandala */}
      <section className="mci-diagram-wrap" aria-label="Five-stage chakra">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mci-diagram"
        >
          <ChakraMandala />
        </motion.div>
      </section>

      {/* Walnut "How It Works" accent panel — the ONLY dark surface on this page */}
      <section className="pt-walnut-panel mci-howitworks" aria-label="How MyCraftz works">
        <div className="mci-howitworks-inner">
          <header className="mci-howitworks-header">
            <span className="pt-eyebrow" style={{ color: "#D4AF37" }}>HOW IT WORKS</span>
            <h2 className="mci-howitworks-title">A path you can actually walk.</h2>
          </header>
          <ul className="mci-howitworks-grid" data-testid="mci-how-it-works">
            {HOW_IT_WORKS.map(({ icon: Icon, text }, i) => (
              <li key={i} className="mci-howitworks-item" data-testid={`mci-how-${i}`}>
                <span className="mci-howitworks-icon"><Icon size={16} /></span>
                <span className="mci-howitworks-text">{text}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 5 vintage icon-tiles */}
      <section className="mci-values" aria-label="What MyCraftz offers" data-testid="mci-values">
        {VALUE_TILES.map(({ icon: Icon, label, sub }, i) => (
          <motion.div
            key={label}
            className="pt-card pt-icon-tile mci-value"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`mci-value-${i}`}
          >
            <span className="pt-icon-wrap"><Icon size={22} /></span>
            <span className="pt-icon-label">{label}</span>
            <span className="pt-icon-sub">{sub}</span>
          </motion.div>
        ))}
      </section>

      {/* CTA */}
      <section className="mci-cta-strip" aria-label="Enter MyCraftz">
        <p className="pt-tagline">When you are ready, the path is already drawn.</p>
        <Link to="/mycraftz/stage/jigyasa" className="pt-btn-rust" data-testid="mci-cta-begin">
          Enter MyCraftz <ArrowRight size={14} />
        </Link>
      </section>

      <GlobalChromeRibbon />
    </main>
  );
}
